// Hub registration (Phase 2): the public application form, the staff review, the field verifier's visit, the
// operator's invitation and an active Hub's standing. Every type and path of these APIs lives in this one file, aligned
// with the backend's src/modules/hub-registration (DTOs, hub-registration.types.ts), common/constants/enums.ts and
// common/constants/hub-requirements.ts.
import { HUB_FORM } from '@constants/limits';
import type { UploadMessages, UploadQueueOpts } from '@lib/hooks/useUploadQueue';
import { api, ApiError, send, upload, type UploadOpts } from './client';
import { ERROR_CODE } from './errors';
import { query, type Cursor, type CursorPage, type OffsetPage } from './paging';

// --- enums (backend common/constants/enums.ts) ----------------------------------------------------------------------

/** Who applies: a person, a cooperative (a programme village cooperative is flagged), a BUMDes, a CV, a PT. */
export const APPLICANT_TYPES = ['INDIVIDUAL', 'COOPERATIVE', 'VILLAGE_ENTERPRISE', 'LIMITED_PARTNERSHIP', 'LIMITED_COMPANY'] as const;
export type ApplicantType = (typeof APPLICANT_TYPES)[number];
/** Entities bring their name, NIB, KBLI, tax id and deed number; a person sends none of them (backend ENTITY_TYPES). */
export const isEntityApplicant = (t: ApplicantType | null | undefined) => !!t && t !== 'INDIVIDUAL';

export const HUB_DOCUMENT_KINDS = [
  'IDENTITY_CARD', 'TAX_ID', 'BUSINESS_ID', 'DEED_OF_ESTABLISHMENT', 'LEGAL_ENTITY_DECREE', 'AUTHORITY_LETTER', 'VILLAGE_RECOMMENDATION', 'LAND_PROOF',
  'ENVIRONMENTAL_STATEMENT', 'SITE_PHOTO_FRONT', 'SITE_PHOTO_STORAGE', 'SITE_PHOTO_SURROUNDINGS', 'SITE_SKETCH', 'INTEGRITY_STATEMENT', 'AGREEMENT', 'VISIT_PHOTO',
  'SANCTION_LETTER',
] as const;
export type HubDocumentKind = (typeof HUB_DOCUMENT_KINDS)[number];
/** The file formats a kind takes (by content; requirements.documents[].formats). */
export type DocumentFormat = 'pdf' | 'image';

export const LAND_STATUSES = ['OWN', 'LEASE', 'LOAN'] as const;
export type LandStatus = (typeof LAND_STATUSES)[number];
export const INTERNET_TYPES = ['CELLULAR', 'FIXED_LINE', 'SATELLITE', 'NONE'] as const;
export type InternetType = (typeof INTERNET_TYPES)[number];

export const HUB_APPLICATION_STATUSES = [
  'SUBMITTED', 'NEEDS_REVISION', 'DOCS_OK', 'VISIT_SCHEDULED', 'VISITED', 'VERIFIED', 'APPROVED', 'AGREEMENT_SIGNED', 'ACTIVE', 'REJECTED', 'WITHDRAWN',
] as const;
export type HubApplicationStatus = (typeof HUB_APPLICATION_STATUSES)[number];
/** Final: nothing moves an application on from here. */
export const CLOSED_STATUSES: readonly HubApplicationStatus[] = ['ACTIVE', 'REJECTED', 'WITHDRAWN'];

export const CHECK_RESULTS = ['PASS', 'FAIL', 'NOT_APPLICABLE'] as const;
export type CheckResult = (typeof CHECK_RESULTS)[number];
export type VisitResult = 'PASS' | 'FAIL';
export type VisitKind = 'APPLICATION' | 'REINSPECTION';
export type HubEventActor = 'APPLICANT' | 'STAFF' | 'SYSTEM';

/** Why an application is rejected (backend REJECTION_REASONS); the text beside it is free. */
export const REJECT_REASONS = [
  'INCOMPLETE_DOCUMENTS', 'INVALID_DOCUMENTS', 'FALSE_INFORMATION', 'SITE_UNSUITABLE', 'SAFETY_NOT_MET', 'AREA_ALREADY_SERVED', 'NO_RESPONSE', 'REVISION_LIMIT',
  'DUPLICATE_APPLICATION', 'IMPERSONATION', 'OTHER',
] as const;
export type RejectReasonCode = (typeof REJECT_REASONS)[number];

/** An active Hub's standing (location.status). */
export const HUB_STATUSES = ['ACTIVE', 'SUSPENDED', 'TERMINATED'] as const;
export type HubStatus = (typeof HUB_STATUSES)[number];
/** The sanction ladder; REINSTATE lifts a suspension. */
export const SANCTION_KINDS = ['WARNING_1', 'WARNING_2', 'SUSPEND', 'REINSTATE', 'TERMINATE'] as const;
export type SanctionKind = (typeof SANCTION_KINDS)[number];

/**
 * The fields a revision may name (backend REVISABLE_FIELDS: the submission's own API names), so the applicant's fix
 * form shows exactly these inputs.
 */
export const REVISABLE_FIELDS = [
  'entityName', 'entityBusinessId', 'entityIndustryCode', 'entityTaxId', 'entityDeedNumber',
  'picName', 'picNationalId', 'picPhone', 'picEmail', 'picTaxId',
  'siteVillageCode', 'siteAddress', 'siteLat', 'siteLng', 'landStatus', 'landValidUntil', 'siteAreaM2', 'storageCapacity', 'nearestFacility', 'nearestFacilityDistanceM',
  'gatewayId', 'readerModel', 'internetType', 'signalNote',
  'bankName', 'bankAccount', 'bankHolder',
] as const;
export type RevisableField = (typeof REVISABLE_FIELDS)[number];

/** What can happen to an application (backend HUB_ACTIONS); the detail's `allowedActions` lists the caller's. */
export type HubAction = 'docsOk' | 'requestRevision' | 'resubmit' | 'scheduleVisit' | 'recordVisit' | 'recommend' | 'approve' | 'reject' | 'withdraw' | 'recordAgreement' | 'activate';

// --- public: requirements and regions -------------------------------------------------------------------------------

/** GET /public/hub-registration/requirements: everything the landing page and the form show (the backend's tables). */
export interface HubRequirements {
  /** the programme operator, the PKS counterparty */
  programOperatorName: string;
  applicantTypes: { code: ApplicantType; label: string; entity: boolean; requiredDocuments: HubDocumentKind[]; optionalDocuments: HubDocumentKind[] }[];
  /** the applicant's document kinds with their formats and size caps */
  documents: { kind: HubDocumentKind; label: string; formats: DocumentFormat[]; maxBytes: number }[];
  checklist: ChecklistItem[];
  landStatuses: { code: LandStatus; label: string }[];
  internetTypes: { code: InternetType; label: string }[];
  fields: { field: RevisableField; label: string }[];
  rejectionReasons: { code: RejectReasonCode; label: string }[];
  statuses: { code: HubApplicationStatus; label: string }[];
  /** working days per state with a deadline */
  serviceLevels: { status: HubApplicationStatus; workingDays: number }[];
  /** a KBLI the form may suggest */
  suggestedIndustryCode: string;
  limits: { maxUploadBytes: number; maxPdfBytes: number; uploadTtlHours: number; maxUploadsPerRequest: number; maxRevisions: number; uploadTypes: string[]; gpsAccuracyMaxM: number };
  /** one contact for help and personal-data requests (SUPPORT_EMAIL); null until configured */
  supportEmail: string | null;
  /** the server sends e-mail (SMTP configured): status notices and invitations reach the person in charge by e-mail */
  emailEnabled: boolean;
  /** the personal-data consent (UU PDP; paragraphs apart by blank lines), versioned (the version goes back with the application), the integrity statement and the data controller */
  consent: { version: string; text: string; integrityText: string; privacyNotice: string; controllerName: string };
  /** the Reader and its gateway are the programme's (the form asks for none): what the facilities step says */
  device: { providedByProgram: boolean; text: string };
  /** the registration is free of charge */
  free: boolean;
  /** the anti-bot check (Cloudflare Turnstile) when a provider is configured: its public site key */
  captcha: { required: boolean; siteKey?: string | null };
}
/** One checklist item: mandatory ones must pass, with a photo, for the visit to pass (never "not applicable"). */
export interface ChecklistItem { code: string; label: string; mandatory: boolean }

/** Kinds staff upload (not in the requirements' applicant documents): the backend's DOCUMENT_RULES. */
const STAFF_DOCUMENT_FORMATS: Partial<Record<HubDocumentKind, DocumentFormat[]>> = { AGREEMENT: ['pdf'], VISIT_PHOTO: ['image'], SANCTION_LETTER: ['pdf', 'image'] };

/** What a kind takes and its size cap: from the requirements answer when it lists the kind, else the backend's rule. */
export function documentRule(req: HubRequirements | null, kind: HubDocumentKind): { formats: DocumentFormat[]; maxBytes: number } {
  const d = req?.documents.find((x) => x.kind === kind);
  if (d) return { formats: d.formats, maxBytes: d.maxBytes };
  const formats = STAFF_DOCUMENT_FORMATS[kind] ?? ['pdf', 'image'];
  return { formats, maxBytes: formats.length === 1 && formats[0] === 'image' ? HUB_FORM.maxImageBytes : HUB_FORM.maxPdfBytes };
}

/**
 * The upload session of this page's public uploads (anti-bot check on): the first upload carries a Turnstile token and
 * the server answers a session (X-Upload-Session, 30 min) that the next uploads send instead. Memory only.
 */
let uploadSession: { value: string; until: number } | null = null;
const SESSION_MS = 30 * 60_000 - 60_000;

/** A public upload, with the anti-bot check when `captcha` is given: the session while it lasts, else a fresh token. */
async function publicUpload(kind: HubDocumentKind, body: Blob, opts: UploadOpts, captcha?: () => Promise<string | undefined>): Promise<HubUpload> {
  if (!captcha) return uploadHubDocument(kind, body, opts);
  const attempt = async (useSession: boolean) => {
    const session = useSession && uploadSession && uploadSession.until > Date.now() ? uploadSession.value : null;
    const token = session ? undefined : await captcha();
    const headers: Record<string, string> = { ...opts.headers, ...(session ? { [UPLOAD_SESSION_HEADER]: session } : token ? { [CAPTCHA_HEADER]: token } : {}) };
    return uploadHubDocument(kind, body, {
      ...opts,
      headers,
      onHeaders: (get) => {
        const next = get(UPLOAD_SESSION_HEADER);
        if (next) uploadSession = { value: next, until: Date.now() + SESSION_MS };
        opts.onHeaders?.(get);
      },
    });
  };
  try {
    return await attempt(true);
  } catch (e) {
    // the session ran out or was refused: a fresh token opens a new one (once)
    if (e instanceof ApiError && e.code === ERROR_CODE.CAPTCHA_FAILED && uploadSession) {
      uploadSession = null;
      return attempt(false);
    }
    throw e;
  }
}

/**
 * The upload queue's options for these documents (lib/hooks/useUploadQueue): each kind checked by its own formats and
 * cap, pictures scaled down first. `route`: a staff screen's uploads go to the signed-in route (PKS scan, visit
 * photos, sanction letter, a renewal's land proof), the public form's to the public one, with the anti-bot check when
 * `captcha` is given. `messages` are the screen's words.
 */
export function hubUploadOptions(req: HubRequirements | null, messages: UploadMessages, formatBytes: (n: number) => string, route: { staff: true } | { captcha?: () => Promise<string | undefined> }): UploadQueueOpts<HubDocumentKind> {
  return {
    send: (kind, body, opts) => ('staff' in route ? uploadHubStaffDocument(kind, body, opts) : publicUpload(kind, body, opts, route.captcha)),
    types: req?.limits.uploadTypes ?? HUB_FORM.uploadTypes,
    rule: (k: HubDocumentKind) => documentRule(req, k),
    maxPickBytes: HUB_FORM.maxPickBytes,
    scale: { maxEdge: HUB_FORM.photo.maxEdge, keepBelowBytes: HUB_FORM.photo.keepBelowBytes, quality: HUB_FORM.photo.quality, background: HUB_FORM.photo.flattenOn },
    concurrency: HUB_FORM.uploadConcurrency,
    autoRetries: HUB_FORM.uploadAutoRetries,
    messages,
    formatBytes,
  };
}

/** Public region lists: codes and names only. */
export interface RegionOption { code: string; name: string }

// --- public: the application -----------------------------------------------------------------------------------------

/** The applicant's data in the API's names (backend HubApplicationFieldsDto). Entity fields only for an entity. */
export interface HubApplicationFields {
  applicantType: ApplicantType;
  /** a cooperative of the national village programme (Koperasi Desa/Kelurahan Merah Putih) */
  programCooperative?: boolean;
  entityName?: string;
  entityBusinessId?: string;
  entityIndustryCode?: string;
  entityTaxId?: string;
  entityDeedNumber?: string;
  picName: string;
  picNationalId: string;
  picPhone: string;
  picEmail: string;
  picTaxId: string;
  siteVillageCode: string;
  siteAddress: string;
  siteLat: number;
  siteLng: number;
  landStatus: LandStatus;
  /** a lease or a loan: until when (YYYY-MM-DD) */
  landValidUntil?: string;
  siteAreaM2: number;
  storageCapacity: number;
  nearestFacility?: string;
  nearestFacilityDistanceM?: number;
  gatewayId?: string;
  readerModel?: string;
  internetType: InternetType;
  signalNote?: string;
  /** all three or none */
  bankName?: string;
  bankAccount?: string;
  bankHolder?: string;
}
export interface HubApplicationInput extends HubApplicationFields {
  /** a UUID made once per form: a repeated submit answers the same number, never a second application */
  submitToken: string;
  uploadIds: string[];
  consentVersion: string;
  personalDataConsent: true;
  integrityStatement: true;
  captchaToken?: string;
}
/** POST /public/hub-registration: the number and the tracking code, the code shown this once (stored hashed). */
export interface HubSubmitted { number: string; trackingCode: string; status: HubApplicationStatus; submittedAt: string }
/** POST /public/hub-registration/uploads?kind=: a file in quarantine (24 h) until a submission or a step takes it. */
export interface HubUpload { uploadId: string; kind: HubDocumentKind; size: number; contentType: string; expiresAt: string }

/** What the applicant sees (POST /public/hub-registration/status); nothing about the staff who acted. */
export interface PublicHubStatus {
  number: string;
  status: HubApplicationStatus;
  applicantType: ApplicantType;
  submittedAt: string;
  updatedAt: string;
  /** the current state's deadline */
  dueAt: string | null;
  /** every state reached, oldest first */
  steps: { status: HubApplicationStatus; at: string }[];
  /** what to fix (NEEDS_REVISION only) */
  revisionRequest: { fields: RevisableField[]; documents: HubDocumentKind[]; note: string } | null;
  rejection: { reasonCode: RejectReasonCode; text: string } | null;
  visitScheduledFor: string | null;
  canResubmit: boolean;
  canWithdraw: boolean;
  revisionsLeft: number;
}
/** POST /public/hub-registration/resubmit: only the fields and documents the revision asked for. */
export interface HubResubmitInput { trackingCode: string; phone: string; changes: Partial<Omit<HubApplicationFields, 'applicantType' | 'programCooperative'>>; uploadIds: string[] }

const PUBLIC = '/public/hub-registration';
/** The header the first public upload sends its anti-bot token in (the submission sends `captchaToken` in its body). */
export const CAPTCHA_HEADER = 'X-Captcha-Token';
/** The header the server answers an upload session in, and the next public uploads send it back in. */
export const UPLOAD_SESSION_HEADER = 'X-Upload-Session';
/** POST /hub-uploads?kind=: a staff document (PKS scan, visit photo, sanction letter, a renewal's land proof), signed in, same quarantine. */
export const uploadHubStaffDocument = (kind: HubDocumentKind, body: Blob, opts?: UploadOpts) => upload<HubUpload>(`/hub-uploads${query({ kind })}`, body, opts);
export const hubPublicPaths = {
  requirements: `${PUBLIC}/requirements`,
  provinces: '/public/regions/provinces',
  regencies: (province: string) => `/public/regions/regencies${query({ province })}`,
  /** every child of the parent, by name, as one page (the backend's REGION_LIST.childrenMax holds the largest) */
  districts: (regencyCode: string) => `/public/regions/districts${query({ regencyCode })}`,
  villages: (districtCode: string) => `/public/regions/villages${query({ districtCode })}`,
};
export type RegionOptionPage = OffsetPage<RegionOption>;
/** Every kind goes through the one quarantine (staff kinds too: AGREEMENT, VISIT_PHOTO, SANCTION_LETTER). */
export const uploadHubDocument = (kind: HubDocumentKind, body: Blob, opts?: UploadOpts) => upload<HubUpload>(`${PUBLIC}/uploads${query({ kind })}`, body, opts);
export const submitHubApplication = (body: HubApplicationInput) => send<HubSubmitted>(PUBLIC, body);
export const fetchHubStatus = (trackingCode: string, phoneLast4: string) => send<PublicHubStatus>(`${PUBLIC}/status`, { trackingCode, phoneLast4 });
export const resubmitHubApplication = (body: HubResubmitInput) => send<PublicHubStatus>(`${PUBLIC}/resubmit`, body);
export const withdrawHubApplication = (trackingCode: string, phone: string) => send<PublicHubStatus>(`${PUBLIC}/withdraw`, { trackingCode, phone });

// --- the operator's invitation (activation creates the account; /accept-invite#token=) -------------------------------

export const inspectInvite = (token: string) => send<{ email: string; name: string; expiresAt: string }>('/auth/invite/inspect', { token });
export const acceptInvite = (token: string, password: string) => send<{ email: string }>('/auth/invite/accept', { token, password });

// --- staff: list, summary, detail -----------------------------------------------------------------------------------

/** A row of GET /hub-applications (personal fields masked by key without pii.read). */
export interface HubApplicationRow {
  id: number;
  number: string;
  status: HubApplicationStatus;
  version: number;
  applicantType: ApplicantType;
  programCooperative: boolean;
  entityName: string | null;
  picName: string;
  siteVillageCode: string;
  villageName: string;
  districtName: string;
  regencyName: string;
  provinceCode: string;
  provinceName: string;
  submittedAt: string;
  updatedAt: string;
  dueAt: string | null;
  overdue: boolean;
  revisionCount: number;
  verifierId: number | null;
  verifierName: string | null;
  visitScheduledFor: string | null;
}
export interface HubApplicationFilter {
  /** a tab is a stage of one or more statuses (comma-separated on the wire) */
  status?: readonly HubApplicationStatus[];
  provinceCode?: string;
  regencyCode?: string;
  districtCode?: string;
  villageCode?: string;
  overdue?: boolean;
  q?: string;
  limit?: number;
}
export type HubApplicationPage = CursorPage<HubApplicationRow>;
/** GET /hub-applications/summary: per status, and the open ones past their deadline, in the caller's scope. */
export interface HubApplicationSummary {
  counts: Partial<Record<HubApplicationStatus, number>>;
  /** open applications past their deadline, in all and per state (the tabs' badges) */
  overdue: number;
  overdueByStatus: Partial<Record<HubApplicationStatus, number>>;
  total: number;
}

export interface HubDocument {
  id: number;
  kind: HubDocumentKind;
  contentType: string;
  size: number;
  uploadedAt: string;
  /** a revision replaced it (kept, never deleted) */
  supersededAt: string | null;
  /** opened only with pii.read (identity card, tax id) */
  restricted: boolean;
}
export interface HubApplicationEvent {
  id: number;
  fromStatus: HubApplicationStatus | null;
  toStatus: HubApplicationStatus;
  actorKind: HubEventActor;
  actorId: number | null;
  actorName: string | null;
  note: string | null;
  at: string;
}
export interface ChecklistAnswer { code: string; result: CheckResult; note: string | null; photoDocumentId: number | null }
export interface HubVisit {
  id: number;
  kind: VisitKind;
  applicationId: number | null;
  locationId: number | null;
  verifierId: number | null;
  verifierName: string | null;
  scheduledFor: string;
  visitedAt: string | null;
  gpsLat: number | null;
  gpsLng: number | null;
  gpsAccuracyM: number | null;
  /** metres between the device's position and the site's pin */
  siteDistanceM: number | null;
  checklist: ChecklistAnswer[] | null;
  result: VisitResult | null;
  identityMatched: boolean | null;
  signalOk: boolean | null;
  signalNote: string | null;
  note: string | null;
}
export interface HubAgreement { id: number; number: string; signedOn: string; validUntil: string; counterparty: string; /** null: a renewal's own file (not an application document) */ documentId: number | null; recordedAt: string; recordedBy: number | null; recordedByName: string | null }

/** GET /hub-applications/:id. Personal fields are masked (or null) without `pii.read`. */
export interface HubApplicationDetail extends Omit<HubApplicationRow, 'verifierId' | 'verifierName' | 'visitScheduledFor'> {
  revisionRequest: { fields: RevisableField[]; documents: HubDocumentKind[]; note: string; fromVisit: boolean } | null;
  closedAt: string | null;
  purgedAt: string | null;
  entityBusinessId: string | null;
  entityIndustryCode: string | null;
  entityTaxId: string | null;
  entityDeedNumber: string | null;
  picNationalId: string | null;
  picPhone: string | null;
  picEmail: string | null;
  picTaxId: string | null;
  districtCode: string;
  regencyCode: string;
  siteAddress: string | null;
  siteLat: number | null;
  siteLng: number | null;
  landStatus: LandStatus;
  landValidUntil: string | null;
  siteAreaM2: number;
  storageCapacity: number;
  nearestFacility: string | null;
  nearestFacilityDistanceM: number | null;
  gatewayId: string | null;
  readerModel: string | null;
  internetType: InternetType;
  signalNote: string | null;
  bankName: string | null;
  bankAccount: string | null;
  bankHolder: string | null;
  consentVersion: string;
  consentAt: string;
  integrityAt: string;
  supplierLocationId: number | null;
  supplierName: string | null;
  verifierId: number | null;
  verifierName: string | null;
  visitScheduledFor: string | null;
  docsCheckedBy: number | null;
  docsCheckedByName: string | null;
  docsCheckedAt: string | null;
  recommendedBy: number | null;
  recommendedByName: string | null;
  recommendedAt: string | null;
  approvedBy: number | null;
  approvedByName: string | null;
  approvedAt: string | null;
  decidedBy: number | null;
  decidedByName: string | null;
  reasonCode: RejectReasonCode | null;
  reasonText: string | null;
  /** the Hub created at activation */
  locationId: number | null;
  locationCode: string | null;
  documents: HubDocument[];
  events: HubApplicationEvent[];
  visits: HubVisit[];
  agreement: HubAgreement | null;
  /** what the caller may do now (permissions, scope, state; the server checks again) */
  allowedActions: HubAction[];
  /** other applications of the same person in charge (by NIK), when the server finds any */
  /** listed only with pii.read (null otherwise); counted for everyone */
  duplicates: { id: number; number: string; status: HubApplicationStatus; submittedAt: string }[] | null;
  duplicateCount: number;
  /** other Hubs and open applications in the same village */
  sameVillage: { kind: 'HUB' | 'APPLICATION'; id: number; name: string; status: string }[];
}

/** GET /hub-applications/:id/available-gateways: an unplaced gateway of the programme's stock. */
export interface AvailableGateway { gwId: string; label: string | null; state: 'online' | 'offline' | 'unknown'; lastSeenAt: string | null }

/** A signed document link (valid a few minutes; each view is audited). */
export interface DocumentLink { url: string; expiresAt: string; contentType: string }

export const hubPaths = {
  list: (f: HubApplicationFilter, cursor: Cursor) =>
    `/hub-applications${query({ status: f.status?.join(','), provinceCode: f.provinceCode, regencyCode: f.regencyCode, districtCode: f.districtCode, villageCode: f.villageCode, overdue: f.overdue || undefined, q: f.q, limit: f.limit, ...cursor })}`,
  summary: (provinceCode?: string) => `/hub-applications/summary${query({ provinceCode })}`,
  detail: (id: number | string) => `/hub-applications/${id}`,
  /** active field verifiers of a province (the schedule dialog's choice) */
  verifiers: (provinceCode?: string) => `/users${query({ role: 'field_verifier', provinceCode, status: 'active', limit: 100 })}`,
  mine: (status: 'open' | 'done') => `/hub-visits/mine${query({ status })}`,
  visit: (id: number | string) => `/hub-visits/${id}`,
  hubProfile: (locationId: number) => `/locations/${locationId}/hub-profile`,
  /** unplaced gateways the programme can install at this Hub (searchable) */
  availableGateways: (id: number, q?: string) => `/hub-applications/${id}/available-gateways${query({ q })}`,
  sanctions: (locationId: number) => `/locations/${locationId}/sanctions`,
};

export const fetchHubDocumentUrl = (applicationId: number, documentId: number) => api<DocumentLink>(`/hub-applications/${applicationId}/documents/${documentId}/url`);
/** The verifier's link to a document of its own visit (the identity card for the in-person match). */
export const fetchVisitDocumentUrl = (visitId: number, documentId: number) => api<DocumentLink>(`/hub-visits/${visitId}/documents/${documentId}/url`);

// --- staff: transitions (each with the version read; 409 STALE_VERSION when someone acted since) ----------------------

/** The transition routes under /hub-applications/:id. */
type ActionPath = 'docs-ok' | 'request-revision' | 'schedule-visit' | 'recommend' | 'approve' | 'reject' | 'agreement' | 'commissioning-check' | 'activate' | 'visit';
const act = <T = HubApplicationDetail>(id: number, action: ActionPath, body: object) => send<T>(`/hub-applications/${id}/${action}`, body);
export const hubActions = {
  docsOk: (id: number, version: number, note?: string) => act(id, 'docs-ok', { version, ...(note ? { note } : {}) }),
  requestRevision: (id: number, version: number, b: { fields: RevisableField[]; documents: HubDocumentKind[]; note: string }) => act(id, 'request-revision', { version, ...b }),
  scheduleVisit: (id: number, version: number, b: { verifierId: number; date: string; supplierLocationId: number }) => act(id, 'schedule-visit', { version, ...b }),
  recommend: (id: number, version: number, note: string) => act(id, 'recommend', { version, ...(note ? { note } : {}) }),
  approve: (id: number, version: number, note: string) => act(id, 'approve', { version, ...(note ? { note } : {}) }),
  reject: (id: number, version: number, reasonCode: RejectReasonCode, text: string) => act(id, 'reject', { version, reasonCode, text }),
  agreement: (id: number, version: number, b: { number: string; signedOn: string; validUntil: string; uploadId: string }) => act(id, 'agreement', { version, ...b }),
  commissioningCheck: (id: number, gatewayId: string) => act<CommissioningResult>(id, 'commissioning-check', { gatewayId }),
  activate: (id: number, version: number, b: { gatewayId: string; hubCode?: string; hubName?: string }) => act<HubActivated>(id, 'activate', { version, ...b }),
  /** an application's visit, by its assigned verifier */
  visit: (id: number, body: VisitInput & { version: number }) => act(id, 'visit', body),
};
/** POST /:id/commissioning-check: the Reader's state, nothing written. */
export interface CommissioningResult { gatewayId: string | null; passed: boolean; issues: { code: string; message: string }[]; lastHeartbeatAt: string | null; state: string | null }
/**
 * An operator's invitation: e-mailed when the server has SMTP (`sentTo` = the masked address), otherwise handed over by
 * the admin (`sentTo` null, `manualLink` = the one-time link).
 */
export interface InviteSent { sentTo: string | null; expiresAt: string; manualLink?: string }
/** POST /:id/activate: the new Hub and the e-mailed invitation of its operator. */
export interface HubActivated { application: HubApplicationDetail; locationId: number; locationCode: string; operator: { userId: number; email: string }; invite: InviteSent }

// --- the field verifier ----------------------------------------------------------------------------------------------

/** GET /hub-visits/mine?status=open|done (open: soonest first). */
export interface AssignedVisit {
  id: number;
  kind: VisitKind;
  scheduledFor: string;
  visitedAt: string | null;
  result: VisitResult | null;
  applicationId: number | null;
  applicationNumber: string | null;
  /** sent back with the result of an application's visit */
  applicationVersion: number | null;
  applicationStatus: HubApplicationStatus | null;
  locationId: number | null;
  locationName: string | null;
  entityName: string | null;
  picName: string | null;
  villageName: string;
  districtName: string;
  regencyName: string;
  siteAddress: string | null;
  siteLat: number | null;
  siteLng: number | null;
}
/** GET /hub-visits/:id: what the verifier needs on site. */
export interface AssignedVisitDetail extends AssignedVisit {
  picPhone: string | null;
  storageCapacity: number | null;
  landStatus: LandStatus | null;
  gatewayId: string | null;
  readerModel: string | null;
  internetType: InternetType | null;
  /** the documents the visit needs (identity card, site photos and sketch, land proof) */
  documents: HubDocument[];
  visit: HubVisit;
}
/** The visit's result (backend VisitResultDto). */
export interface VisitInput {
  checklist: { code: string; result: CheckResult; note?: string; photoUploadId?: string }[];
  gps: { lat: number; lng: number; accuracyM: number };
  identityMatched: boolean;
  signalTest: { ok: boolean; note?: string };
  photoUploadIds?: string[];
  note?: string;
  /** a re-inspection has no version to guard it: a UUID made once per report, so a resend is recorded once */
  submitToken?: string;
}
/** A re-inspection's result (an application's visit goes to POST /hub-applications/:id/visit with the version). */
export const reportReinspection = (visitId: number, body: VisitInput) => send<HubVisit>(`/hub-visits/${visitId}/result`, body);

// --- an active Hub: standing and sanctions --------------------------------------------------------------------------

export interface HubSanction { id: number; kind: SanctionKind; reason: string; hasDocument: boolean; createdBy: number | null; createdByName: string | null; createdAt: string }
/** GET /locations/:id/hub-profile */
export interface HubProfile {
  locationId: number;
  status: HubStatus;
  lastSanction: HubSanction | null;
  /** what the ladder allows next for this caller */
  nextSanctions: SanctionKind[];
  applicationId: number | null;
  applicationNumber: string | null;
  agreement: HubAgreement | null;
  /** how many PKS the Hub has had (renewals included) */
  agreementCount: number;
  landValidUntil: string | null;
  lastInspectionOn: string | null;
  inspectionDueOn: string;
  openReinspection: HubVisit | null;
}
/** POST /locations/:id/sanctions: answers the sanctions, newest first. */
export const addHubSanction = (locationId: number, body: { kind: SanctionKind; reason: string; uploadId?: string }) => send<HubSanction[]>(hubPaths.sanctions(locationId), body);

/** POST /locations/:id/agreement: a renewed (or first) PKS of an active Hub; answers its profile. */
export const renewHubAgreement = (locationId: number, body: { number: string; signedOn: string; validUntil: string; uploadId: string }) => send<HubProfile>(`/locations/${locationId}/agreement`, body);
/** POST /locations/:id/land: the land's lease or loan renewed (its new end and the proof); answers the profile. */
export const renewHubLand = (locationId: number, body: { validUntil: string; uploadId: string }) => send<HubProfile>(`/locations/${locationId}/land`, body);
/** POST /users/:id/reinvite: a new invitation for a Hub operator who never used theirs, e-mailed by the server. */
export const reinviteUser = (userId: number) => send<{ invite: InviteSent }>(`/users/${userId}/reinvite`, {});
